import type { ChangeEvent, CSSProperties, FC } from 'react'
import { useCallback, useRef, useState } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Alert, Box, Typography } from '@mui/material'
import { FileDownloadOutlined, FileUploadOutlined, RotateRightOutlined } from '@mui/icons-material'
import {
  actionButtonSx,
  buttonLabelSx,
  contentSx,
  descriptionSx,
  iconSx,
  sectionDescriptionSx,
  sectionTitleSx,
  subContainerSx,
} from '@/features/settings/components/settingsSectionStyles'
import { StorageModeCheckbox } from '@/common/components'
import { useEncryptionSettings } from '../hooks'
import { KeyRingTable } from './KeyRingTable'
import { RotateKeyDialog } from './RotateKeyDialog'

const MAX_KEY_RING_FILE_SIZE = 1024 * 1024

export const EncryptionSection: FC = () => {
  const {
    keyRingStatus,
    activeKeyVersion,
    storageMode,
    keys,
    isRotating,
    rotateKey,
    setStorageMode,
    exportKeyRing,
    importKeyRing,
  } = useEncryptionSettings()

  const [isRotateDialogOpen, setIsRotateDialogOpen] = useState(false)
  const [importError, setImportError] = useState<string | null>(null)

  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleOpenRotateDialog = useCallback(() => {
    setIsRotateDialogOpen(true)
  }, [])

  const handleCloseRotateDialog = useCallback(() => {
    setIsRotateDialogOpen(false)
  }, [])

  const handleConfirmRotate = useCallback(() => {
    void rotateKey().then(() => {
      setIsRotateDialogOpen(false)
    })
  }, [rotateKey])

  const handleDownloadBackup = useCallback(() => {
    exportKeyRing()
  }, [exportKeyRing])

  const handleFileInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (!file) return

      setImportError(null)

      if (!file.type.includes('json') && !file.name.endsWith('.json')) {
        setImportError('Invalid file type. Please select a JSON file.')
        return
      }

      if (file.size > MAX_KEY_RING_FILE_SIZE) {
        setImportError('File too large. Key ring files should be under 1MB.')
        return
      }

      void importKeyRing(file)
        .catch((err: unknown) => {
          setImportError(err instanceof Error ? err.message : 'Failed to import key ring file.')
        })
        .finally(() => {
          if (fileInputRef.current) {
            fileInputRef.current.value = ''
          }
        })
    },
    [importKeyRing],
  )

  const handleImportClick = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const isLoaded = keyRingStatus === 'loaded'

  return (
    <>
      <Box>
        <Typography variant="body1" sx={sectionTitleSx}>
          Encryption Keys
        </Typography>
        <Typography variant="body2" sx={sectionDescriptionSx}>
          Manage your end-to-end encryption keys.
        </Typography>

        {isLoaded ? (
          <>
            <Box sx={subContainerSx}>
              <Box sx={toolbarSx}>
                <Typography variant="body2" sx={toolbarTitleSx}>
                  Key Versions
                </Typography>
                <Box sx={toolbarActionsSx}>
                  <Box
                    component="button"
                    type="button"
                    onClick={handleOpenRotateDialog}
                    sx={actionButtonSx}
                    aria-label="Rotate encryption key"
                  >
                    <RotateRightOutlined sx={iconSx} aria-hidden="true" />
                    <Typography variant="body2" sx={buttonLabelSx}>
                      Rotate
                    </Typography>
                  </Box>
                  <Box
                    component="button"
                    type="button"
                    onClick={handleDownloadBackup}
                    sx={actionButtonSx}
                    aria-label="Download key ring backup"
                  >
                    <FileDownloadOutlined sx={iconSx} aria-hidden="true" />
                    <Typography variant="body2" sx={buttonLabelSx}>
                      Backup
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <KeyRingTable keys={keys} activeKeyVersion={activeKeyVersion} />
            </Box>

            <Box sx={storageModeContainerSx}>
              <StorageModeCheckbox currentMode={storageMode} onModeChange={setStorageMode} />
            </Box>
          </>
        ) : (
          <Box sx={contentSx}>
            <Typography variant="body2" sx={descriptionSx}>
              No encryption keys configured.
            </Typography>
            <Box
              component="button"
              type="button"
              onClick={handleImportClick}
              sx={actionButtonSx}
              aria-label="Import key ring file"
            >
              <FileUploadOutlined sx={iconSx} aria-hidden="true" />
              <Typography variant="body2" sx={buttonLabelSx}>
                Import Key Ring
              </Typography>
            </Box>
          </Box>
        )}

        {importError && (
          <Alert severity="error" sx={alertSx} onClose={() => setImportError(null)}>
            {importError}
          </Alert>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept=".json,application/json"
          onChange={handleFileInputChange}
          style={hiddenInputStyle}
          aria-hidden="true"
          tabIndex={-1}
        />
      </Box>

      <RotateKeyDialog
        open={isRotateDialogOpen}
        onClose={handleCloseRotateDialog}
        onConfirm={handleConfirmRotate}
        isRotating={isRotating}
      />
    </>
  )
}

const toolbarSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  mb: 1,
}

const toolbarTitleSx: SxProps<Theme> = {
  color: 'text.secondary',
  fontSize: '0.8rem',
  fontWeight: 500,
}

const toolbarActionsSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
}

const storageModeContainerSx: SxProps<Theme> = {
  border: 1,
  borderColor: 'divider',
  borderRadius: 1,
  p: 2,
  mt: 2,
}

const alertSx: SxProps<Theme> = {
  mt: 2,
}

const hiddenInputStyle: CSSProperties = {
  position: 'absolute',
  width: 0,
  height: 0,
  overflow: 'hidden',
  opacity: 0,
}
